import { useRef, type ChangeEvent, type FocusEvent } from 'react';

import TextField, {
	type TextFieldProps,
} from '@liquidweb-dev/undercurrent/TextField';

import { useInput } from 'components/LWForm/LWForm';
import calculateNewCursorPosition from 'utility/calculateNewCursorPosition';
import clean from 'utility/cleanDigits';

function formatExpirationDate(value: string, cursorPosition: number | null) {
	if (value.length - 1 === 5 && cursorPosition) {
		return {
			formattedValue:
				value.slice(0, cursorPosition - 1) + value.slice(cursorPosition),
			newCursorPosition: value.length - 1,
		};
	}

	const cleanValue = clean(value);

	const formattedValue =
		cleanValue.length >= 3
			? `${cleanValue.slice(0, 2)}/${cleanValue.slice(2, 4)}`
			: cleanValue;

	const newCursorPosition = calculateNewCursorPosition(
		value,
		formattedValue,
		cursorPosition,
	);

	return { formattedValue, newCursorPosition };
}

export type HookedTextFieldMonthProps = Omit<TextFieldProps, 'select'>;

const HookedTextFieldMonth = ({
	error: errorProp,
	helperText,
	name,
	onBlur,
	onChange,
	...rest
}: HookedTextFieldMonthProps) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const { value, onValueChange, error, validate } = useInput(name!);

	if (!name) {
		return null;
	}

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		if (!event.target) {
			return;
		}

		const { value: newValue, selectionStart } = event.target;

		const { formattedValue, newCursorPosition } = formatExpirationDate(
			newValue,
			selectionStart,
		);

		onValueChange(formattedValue);
		setTimeout(() => {
			if (inputRef.current) {
				inputRef.current.setSelectionRange(
					newCursorPosition,
					newCursorPosition,
				);
			}
		}, 0);

		if (onChange) {
			onChange(event);
		}

		// validate on input change only if there is an existing error
		if (!error) {
			return;
		}

		validate(event.target);
	};

	const handleBlur = (event: FocusEvent<HTMLInputElement, Element>) => {
		if (!event.target) {
			return;
		}

		if (onBlur) {
			onBlur(event);
		}

		validate(event.target);
	};

	return (
		<TextField
			error={Boolean(error || errorProp)}
			helperText={error || errorProp || helperText}
			name={name}
			onBlur={handleBlur}
			onChange={handleChange}
			placeholder="MM/YY"
			slotProps={{
				htmlInput: { ref: inputRef },
			}}
			{...rest}
			value={value}
		/>
	);
};

export default HookedTextFieldMonth;
